import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map, Observable } from 'rxjs';
import { buildSorting } from 'src/app/components/user-relation-requests/components/manage-relation-request/manage-relation-request.helper';
import { PaginatableListOfAdmins } from 'src/app/swagger-api';
import AppValues from '../../../../common/app.values';
import { SortingOrderType } from '../../../../interfaces/search.interface';
import { TableCellOnActionEvent, UserTableConstructorInfo } from '../../../../interfaces/tables.interfaces';
import {
  DEFAULT_SORT_OPTIONS,
  getSortingOrderType,
  isAscendingIcon,
  SortCategories,
  SortOptions,
} from '../../../../views/sort-elements/sort-drop-down/sort-drop-down.helpers';
import { CellActionTypes } from '../../../tables/components/cell-actions.type';
import { AdminService } from '../../admin.service';
import { AdminTableFactory } from './admins-table-factory';

export interface TableViewModel {
  data: UserTableConstructorInfo;
  totalCount: number;
  pagination: boolean;
  allowedSorting: boolean;
}

@Component({
  selector: 'app-admins-list',
  templateUrl: './admins-list.component.html',
  styleUrls: ['./admins-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminsListComponent implements OnInit {
  public readonly SORT_OPTIONS: SortOptions[] = DEFAULT_SORT_OPTIONS;

  public pageSize = AppValues.ADMINS_PAGE_SIZE;
  public sorting: SortCategories = SortCategories.NAME_ASC;

  public currentPage = 1;

  public currentSortCategory: SortingOrderType = SortingOrderType.NAME;
  public ascending = true;

  public get offset(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  public get tableViewModel$(): Observable<TableViewModel> {
    return this.adminService.adminsListChanged.pipe(
      map((listOfAdmins: PaginatableListOfAdmins) => ({
        data: new AdminTableFactory().buildTableData(listOfAdmins.items),
        totalCount: listOfAdmins.total_count,
        allowedSorting: true,
        pagination: true,
      })),
    );
  }

  private adminService = inject(AdminService);

  public constructor() {
    this.adminService.adminDeleted.pipe(takeUntilDestroyed()).subscribe(() => {
      this.getAdminsList();
    });
  }

  public ngOnInit(): void {
    this.getAdminsList();
  }

  public onSortParamChanged(orderType: SortingOrderType): void {
    this.currentSortCategory = orderType;
    this.sorting = buildSorting(this.sorting, orderType);
    this.ascending = isAscendingIcon(this.sorting);
    this.getAdminsList();
  }

  public toggleSortingOrderByType(sortOptions: SortOptions): void {
    this.sorting = sortOptions.sortCategory;
    this.ascending = isAscendingIcon(sortOptions.sortCategory);
    this.currentSortCategory = getSortingOrderType(sortOptions.sortCategory);
    this.getAdminsList();
  }

  public onTableAction(event: TableCellOnActionEvent): void {
    const actions: { [key: string]: () => void } = {
      [CellActionTypes.REMOVE]: () => this.openRemoveAdminDialog(event.entityId),
    };

    actions[event.type] && actions[event.type]();
  }

  public openRemoveAdminDialog(userId: string): void {
    this.adminService.openRemoveAdminDialog(userId);
  }

  public onPageChanged(page: number): void {
    this.currentPage = page;
    this.getAdminsList();
  }

  public getAdminsList(): void {
    this.adminService.getAdminsList(this.sorting, this.offset, this.pageSize);
  }
}
